// One open Move work panel's state (#6772, epic #6552): the per-item previews, the ticks, the
// selection's preview, the chosen machine and the send. `components/machine-card/MoveWorkPanel.tsx`
// renders it; every rule is `./move-work-panel.ts`'s, every verdict the server's.
//
// THE PREVIEWS. On open, each listed item is previewed ALONE (`MovePage.previewItems`, which
// never touches Move to's one session); an item is disabled with its short reason while its own
// preview's source refusal stands at the page clock, and a `cooling_down` one comes back once
// its `free_at` passes (the clock is kept ticking to it). Every change to the ticked set
// previews the selection again, ONE call, which fills Move to (each option's refusal and CPU),
// the footer (`cost`, the source's `left`) and the why line. An answer to an older selection,
// or one landing after the panel closed, is dropped (a generation and a `closed` flag).
//
// EVERY CACHED ANSWER NAMES WHAT SUPERSEDES IT (#6772 review round 8); none is kept by a
// "seen" set. An item's preview carries the `orderStamp` it was asked at and the fleet read's
// signature of that item (`itemSignature`): a newer fleet read showing the item changed asks
// it again, and only the newest ask writes. A refused send carries the order it was sent at:
// a selection preview asked after it that confirms the destination (`previewConfirms`)
// supersedes it, as does a new selection.
//
// THE SEND is ONE `MovePage.moveMany` through the page guard. "Transferring…" shows for at least
// 560 ms (the prototype's hold in its mwGo, `:2688-2692`, kept as a minimum display, not a delay
// before acting); a 201 then closes the panel. A refusal keeps it open: the refused option
// keeps the REFUSAL itself (never its sentence, so its countdown reads the live clock and it
// lapses at its `free_at`), and the selection is previewed again.

import {
  type Accessor,
  createEffect,
  createMemo,
  createSignal,
  on,
  onCleanup,
  untrack,
} from 'solid-js';
import { t } from '../i18n';
import type { UtilBox } from './fleetUtilization';
import { MACHINE_STATUS } from './machine-labels';
import type { MoveItem, MovePreviewTarget, MoveRefusalWire } from './move-calls';
import { standingRefusal } from './move-copy';
import { epicSubject, jobSubject, machineOnline } from './move-items';
import type { MovePage, PreviewState } from './move-page';
import { enterTarget, type MenuOption, menuOptions, orderOptions } from './move-to-menu';
import {
  coveredJobs,
  drainTicks,
  itemRefusal,
  itemSignature,
  type PanelItem,
  panelItems,
  previewConfirms,
  previewReaskOnRead,
  selectionCounts,
  selectionItems,
  selectionWhat,
} from './move-work-panel';
import { orderStamp } from './read-order';

/** "Transferring…" stays on screen at least this long (the prototype's hold). */
export const TRANSFER_MIN_MS = 560;

/** One machine of "Move to": an option of the selection's preview, or of the fleet read. */
export type WorkTarget = MenuOption<MovePreviewTarget | null> & {
  readonly offline: boolean;
};

const sameKeys = (a: readonly PanelItem[], b: readonly PanelItem[]): boolean =>
  a.length === b.length && a.every((x, i) => x.key === b[i]?.key && x.label === b[i]?.label);

/** One item's preview: its answer, the order it was asked at, the fleet's item it was for. */
type ItemAnswer = {
  readonly state: PreviewState;
  readonly order: number;
  readonly sig: string;
  /** The fleet read it was asked on (`MovePage.fleetRead`). */
  readonly read: number;
};

/**
 * The per-item previews: each listed item asked alone, and asked again whenever a newer fleet
 * read shows that item changed; Drain's ticks once every one answered.
 */
function itemPreviews(
  page: MovePage,
  box: Accessor<UtilBox>,
  items: Accessor<readonly PanelItem[]>,
) {
  const [answers, setAnswers] = createSignal<ReadonlyMap<string, ItemAnswer>>(new Map());
  let closed = false;
  onCleanup(() => {
    closed = true;
  });
  createEffect(() => {
    const b = box();
    const read = page.fleetRead();
    const now = untrack(page.clock);
    const listed = new Set(items().map((it) => it.key));
    // An item the fleet no longer lists has no answer: a re-listed one is asked afresh.
    if ([...untrack(answers).keys()].some((k) => !listed.has(k))) {
      setAnswers((m) => new Map([...m].filter(([k]) => listed.has(k))));
    }
    for (const it of items()) {
      const sig = itemSignature(b, it.item);
      const cur = untrack(answers).get(it.key);
      // Standing while the fleet still shows the item it answered for, unless it holds a refusal
      // no row field clears (`READ_REASKED`): that one is asked again on any newer fleet read.
      const reread = cur !== undefined && read > cur.read && previewReaskOnRead(cur.state, now);
      if (cur?.sig === sig && !reread) continue;
      const order = orderStamp();
      setAnswers((m) => new Map(m).set(it.key, { state: { status: 'pending' }, order, sig, read }));
      void page.previewItems(untrack(box).id, [it.item]).then((state) => {
        // Only the newest ask for this item writes; an older answer is superseded by it.
        if (closed || untrack(answers).get(it.key)?.order !== order) return;
        // Its refusal's wait counts from the moment it answered, not the clock's last tick.
        page.readClock();
        setAnswers((m) => new Map(m).set(it.key, { state, order, sig, read }));
      });
    }
  });
  const previews = createMemo(
    (): ReadonlyMap<string, PreviewState> =>
      new Map([...answers()].map(([k, a]) => [k, a.state] as const)),
  );
  /** An item's own source refusal standing at the page clock; the clock ticks to its end. */
  const refusalOf = (key: string): MoveRefusalWire | null => {
    const r = itemRefusal(previews().get(key), page.clock());
    if (r?.free_at != null) page.tickUntil(Date.parse(r.free_at));
    return r;
  };
  return {
    previews,
    refusalOf,
    settled: () => [...previews().values()].every((p) => p.status !== 'pending'),
  };
}

const sameItems = (a: readonly MoveItem[], b: readonly MoveItem[]): boolean =>
  a.length === b.length && a.every((x, i) => x.kind === b[i]?.kind && x.id === b[i]?.id);

/** The selection's preview and the order it was asked at. */
type SelectionAnswer = {
  readonly state: PreviewState;
  readonly order: number;
  /** The fleet read it was asked on (`MovePage.fleetRead`). */
  readonly read: number;
};

/** The selection's ONE preview, asked again on every change; only the newest ask writes. */
function selectionPreview(
  page: MovePage,
  box: Accessor<UtilBox>,
  sel: Accessor<readonly MoveItem[]>,
) {
  const [answer, setAnswer] = createSignal<SelectionAnswer | null>(null);
  let closed = false;
  onCleanup(() => {
    closed = true;
  });
  const ask = (): void => {
    const items = untrack(sel);
    if (items.length === 0) {
      setAnswer(null);
      return;
    }
    const order = orderStamp();
    const read = untrack(page.fleetRead);
    setAnswer({ state: { status: 'pending' }, order, read });
    void page.previewItems(untrack(box).id, items).then((s) => {
      if (closed || untrack(answer)?.order !== order) return;
      page.readClock();
      setAnswer({ state: s, order, read });
    });
  };
  createEffect(on(sel, ask));
  // An answer holding a refusal no row field clears (`READ_REASKED`) is asked again on the next
  // fleet read, so it is never staler than one fleet poll.
  createEffect(
    on(page.fleetRead, (read) => {
      const a = untrack(answer);
      if (a !== null && read > a.read && previewReaskOnRead(a.state, untrack(page.clock))) ask();
    }),
  );
  return { state: (): PreviewState | null => answer()?.state ?? null, answer, ask };
}

/** A refused send of the selection: the refusal and the order the send was made at. */
type SendRefusal = { readonly refusal: MoveRefusalWire; readonly order: number };

/** "Move to": the selection preview's targets, else the fleet read's other own-org machines. */
function workTargets(
  page: MovePage,
  box: Accessor<UtilBox>,
  sel: Accessor<SelectionAnswer | null>,
  refused: Accessor<ReadonlyMap<string, SendRefusal>>,
): Accessor<readonly WorkTarget[]> {
  const stateWord = (id: string): string => {
    const b = page.boxOf(id);
    return b === undefined ? '' : t(MACHINE_STATUS[b.verdict.status]);
  };
  const offline = (id: string): boolean => {
    const b = page.boxOf(id);
    return b !== undefined && !machineOnline(b);
  };
  return createMemo((): readonly WorkTarget[] => {
    const a = sel();
    const p = a?.state ?? null;
    if (a !== null && p !== null && p.status === 'ready') {
      const now = page.clock();
      const preview = p.preview;
      /** A send's refusal, unless a preview asked after it confirms this target. */
      const sendRefusal = (id: string): MoveRefusalWire | undefined => {
        const r = refused().get(id);
        if (r === undefined) return undefined;
        return a.order > r.order && previewConfirms(preview, id, now) ? undefined : r.refusal;
      };
      return menuOptions(preview.targets, page.boxOf, stateWord, (target) => {
        const r =
          standingRefusal(sendRefusal(target.runner_id), now) ??
          standingRefusal(target.refusal, now);
        if (r?.free_at != null) page.tickUntil(Date.parse(r.free_at));
        return r === null ? null : page.textOf(r, target.cpu?.after ?? null);
      }).map((o) => ({ ...o, offline: offline(o.machine.id) }));
    }
    // Nothing ticked (or not answered yet): every other own-org machine, its CPU now.
    const others = page
      .boxes()
      .filter((b) => b.id !== box().id && b.via_org === null)
      .map((b): WorkTarget => {
        const ratio = b.verdict.signals.cpu.ratio;
        return {
          machine: {
            id: b.id,
            name: b.display_name,
            shortId: b.id.slice(0, 8),
            server: b.server.label ?? b.server.address,
            stateWord: stateWord(b.id),
          },
          target: null,
          canTake: machineOnline(b),
          cpuBefore: ratio === null ? null : Math.round(ratio * 100),
          why: null,
          offline: !machineOnline(b),
        };
      });
    return orderOptions(others);
  });
}

/** One open panel: everything `MoveWorkPanel` renders and does. */
export function createMoveWork(
  page: MovePage,
  box: Accessor<UtilBox>,
  o: { readonly drain: boolean; readonly onMoved: (firstKey: string | null) => void },
) {
  const items = createMemo(() => panelItems(box(), t), undefined, { equals: sameKeys });
  const own = itemPreviews(page, box, items);
  const [ticked, setTicked] = createSignal<ReadonlySet<string>>(new Set());
  /** Ticked and movable now: a tick whose own refusal came back no longer goes. */
  const live = createMemo(
    () => new Set([...ticked()].filter((k) => own.refusalOf(k) === null)),
    undefined,
    { equals: (a, b) => a.size === b.size && [...a].every((k) => b.has(k)) },
  );
  const covered = createMemo(() => coveredJobs(items(), live()));
  const sel = createMemo(() => selectionItems(items(), live()), undefined, { equals: sameItems });
  const counts = createMemo(() => selectionCounts(items(), live()));
  const selPreview = selectionPreview(page, box, sel);
  const [refused, setRefused] = createSignal<ReadonlyMap<string, SendRefusal>>(new Map());
  // Each selection is a generation: an answer to a send carries the one it was SENT for, and
  // writes only while that is still the selection (#6772 review round 6). A fleet re-read that
  // drops a ticked item starts a new one, as a tick does.
  let selGen = 0;
  createEffect(
    on(
      sel,
      () => {
        selGen += 1;
        setRefused(new Map());
      },
      { defer: true },
    ),
  );
  const targets = workTargets(page, box, selPreview.answer, refused);
  const [chosen, setChosen] = createSignal<string | null>(null);
  // The first machine that can take it is chosen when none is, or the chosen one now refuses.
  createEffect(() => {
    if (sel().length === 0 || selPreview.state()?.status !== 'ready') return;
    const cur = targets().find((x) => x.machine.id === untrack(chosen));
    if (cur === undefined || !cur.canTake) setChosen(enterTarget(targets())?.machine.id ?? null);
  });
  // Drain: once every item has answered, tick everything the server says can move.
  if (o.drain) {
    let done = false;
    createEffect(() => {
      if (done || items().length === 0 || !own.settled()) return;
      done = true;
      // Judged at NOW: a cooling-down refusal whose wait just passed is movable again.
      const ticks = drainTicks(items(), own.previews(), Date.now());
      setTicked(ticks);
      const name = box().display_name;
      page.announce(
        ticks.size > 0
          ? t('fleet.move.drain_selected', { name })
          : t('fleet.move.drain_nothing', { name }),
      );
    });
  }
  // One item reads as its own subject ("The Coder job", "Epic X and its 2 jobs"); more, as counts.
  const what = (): string => {
    const only = sel().length === 1 ? sel()[0] : undefined;
    const b = box();
    if (only?.kind === 'task') {
      const job = b.jobs?.find((j) => j.id === only.id);
      if (job !== undefined) return jobSubject(b, job, t).what;
    }
    if (only?.kind === 'epic') {
      const epic = b.jobs?.find((j) => j.epic?.plan_id === only.id)?.epic;
      if (epic != null) return epicSubject(b, epic, t).what;
    }
    return selectionWhat(counts(), t);
  };
  const send = createSend(page, box, {
    sel,
    counts,
    what,
    chosen,
    generation: () => selGen,
    refuse: (gen, id, r, order) => {
      if (gen !== selGen) return;
      setRefused((m) => new Map(m).set(id, { refusal: r, order }));
      // Asked after the send: it supersedes the refusal if it confirms the destination.
      selPreview.ask();
    },
    onMoved: o.onMoved,
  });
  return {
    items,
    previews: own.previews,
    refusalOf: own.refusalOf,
    ticked: live,
    covered,
    counts,
    sel,
    selPreview: selPreview.state,
    targets,
    chosen,
    choose: setChosen,
    toggle(key: string): void {
      // The ticks hold while a move is sending: its answer belongs to the selection it sent.
      if (send.sending() || own.refusalOf(key) !== null || covered().has(key)) return;
      setTicked((s) => {
        const next = new Set(s);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    },
    ...send,
  };
}

/** The send: ONE `moveMany`, "Transferring…" held at least 560 ms, a 201 closing the panel. */
function createSend(
  page: MovePage,
  box: Accessor<UtilBox>,
  o: {
    readonly sel: Accessor<readonly MoveItem[]>;
    readonly counts: Accessor<{ readonly epics: number; readonly jobs: number }>;
    readonly what: Accessor<string>;
    readonly chosen: Accessor<string | null>;
    /** The selection's generation now; a send captures it. */
    readonly generation: () => number;
    /**
     * A refusal of the send made for generation `gen` at `order`: written only if that is still
     * the selection.
     */
    readonly refuse: (gen: number, targetId: string, r: MoveRefusalWire, order: number) => void;
    readonly onMoved: (firstKey: string | null) => void;
  },
) {
  const [sending, setSending] = createSignal(false);
  let hold: ReturnType<typeof setTimeout> | undefined;
  let closed = false;
  onCleanup(() => {
    closed = true;
    if (hold !== undefined) clearTimeout(hold);
    hold = undefined;
  });
  const go = async (): Promise<void> => {
    const items = o.sel();
    const gen = o.generation();
    const target = o.chosen();
    if (untrack(sending) || items.length === 0 || target === null) return;
    const started = Date.now();
    setSending(true);
    const outcome = await page.moveMany({
      sourceId: box().id,
      items,
      targetId: target,
      key: `work:${box().id}`,
      what: o.what(),
      many: o.counts().jobs + o.counts().epics > 1,
      zone: items.some((i) => i.kind === 'epic') ? 'mission' : 'now',
    });
    if (closed) return;
    if (outcome.kind !== 'moved') {
      setSending(false);
      if (outcome.kind === 'refused' || outcome.kind === 'wait') {
        // Stamped as it arrives: only a preview asked after the refusal can supersede it.
        o.refuse(gen, target, outcome.refusal, orderStamp());
      }
      return;
    }
    const first = items[0];
    const key = first === undefined ? null : `${target}|${first.kind}:${first.id}`;
    const rest = TRANSFER_MIN_MS - (Date.now() - started);
    if (rest <= 0) {
      o.onMoved(key);
      return;
    }
    hold = setTimeout(() => {
      hold = undefined;
      if (!closed) o.onMoved(key);
    }, rest);
  };
  return { sending, go };
}
